import { Collapse, DetectionZone, Device, Facility, LaneLine, ScreenLine, Target, Targets } from "@app/shared";
import { isPvrExportEnabled, RecentTargetsMethods } from "@app/shared/src/components/LiveView";
import { add } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import _ from "lodash";
import { ChangeEventHandler, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSessionStorage } from "react-use";
import { useFacilityCenter } from "../../../helpers/mapHelpers";
import { useDialog } from "../../../hooks";
import { useLiveViewHub } from "../../../hooks/useLiveViewHub";
import { CheckboxInput, DateInput, DropDownMenu, IconLink, Icons, LiveStatusChip, PageContainer, Select, SelectOption } from "../../shared";
import { FacilityBackgroundView, getFacilityBackgroundType } from "../../shared/LayoutDesigner/FacilityBackgroundView";
import { TargetMapShape } from "../../shared/maps";
import { MapShape } from "../../shared/maps/MapShape";
import { OmniGoogleMap } from "../../shared/maps/OmniGoogleMap";
import { TargetDetailsDialog } from "./TargetDetailsDialog";

const RecentTargetsTable = lazy(() =>
    import("@app/shared/src/components/LiveView/RecentTargetsTable")
        .then(module => ({ default: module.RecentTargetsTable }))
);

type FacilityLiveViewProps = {
    facility: Facility;
};

const timeOptions: SelectOption[] = [
    { label: "1 Hours", value: 1 },
    { label: "4 Hours", value: 4 },
    { label: "8 Hours", value: 8 },
    { label: "12 Hours", value: 12 },
    { label: "24 Hours", value: 24 },
    { label: "Date Range", value: -1 }
];

export const FacilityLiveView: React.FC<FacilityLiveViewProps> = ({ facility }) => {
    const useMapBackground = getFacilityBackgroundType(facility) === "map";
    const [hoursToShow, setHoursToShow] = useState(1);
    const [hideOverflow, setHideOverflow] = useState(false);

    const [selectedTargets, setSelectedTargets] = useState<{ [key: string]: Target }>({});
    const [dateRange, setDateRange] = useState<[Date?, Date?]>([add(new Date(), { hours: -hoursToShow }), undefined]);

    // Live streaming is only coherent with the default newest-first sort and a rolling
    // (non-fixed) window. Sorting by any other column, or picking a fixed Date Range,
    // pauses the whole live view until the user resumes.
    const [isDefaultSort, setIsDefaultSort] = useState(true);
    const handleSortStateChange = useCallback((isDefault: boolean) => setIsDefaultSort(isDefault), []);
    const isDateRange = !!dateRange[1];
    const isLive = isDefaultSort && !isDateRange;
    const details = useDialog<Target>();
    const [centerCoords, isLoading] = useFacilityCenter(facility);
    const [autoSelect, setAutoSelect] = useState(true);
    const autoSelectRef = useRef(autoSelect);
    const tableRef = useRef<RecentTargetsMethods>(null);
    const [hideDevices, setHideDevices] = useSessionStorage(`facility_liveview_hidedevices_${facility.id}`, false);
    const [hideSL, setHideSL] = useSessionStorage(`facility_liveview_hideSL_${facility.id}`, false);
    const [hideLL, setHideLL] = useSessionStorage(`facility_liveview_hideLL_${facility.id}`, false);
    const [hideDZ, setHideDZ] = useSessionStorage(`facility_liveview_hideDZ_${facility.id}`, false);
    if (!facility.localTimezoneId) {
        timeOptions.pop();
    }

    //Connect our live view
    useLiveViewHub(facility.id, (targets: Target[]) => {
        if (autoSelectRef.current) {
            setSelectedTargets((prev) => {
                const newSelections = { ...prev };
                targets.forEach(target => {
                    newSelections[target.id!] = { ...target, history: ([...target?.history ?? [], ...prev[target.id!]?.history ?? []]).sort((a, b) => b.date!.getTime() - a.date!.getTime()) };
                });
                return newSelections;
            });
        }
        tableRef.current?.addNewTargets(targets, autoSelectRef.current);

    }, !isLive);

    useEffect(() => {
        autoSelectRef.current = autoSelect;
    }, [autoSelect])

    const handleOnTimeOptionChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
        var newVal = parseInt(e.target.value);
        setHoursToShow(newVal);
        if (newVal <= 0) {
            setDateRange([undefined, new Date()]);
        } else {
            setDateRange([add(new Date(), { hours: -newVal }), undefined]);
        }
    };

    const handleResumeLive = () => {
        // Keep the current rolling window; only a fixed Date Range (no live equivalent)
        // falls back to the last hour. Resetting the sort flips isDefaultSort -> live.
        if (isDateRange) {
            setHoursToShow(1);
            setDateRange([add(new Date(), { hours: -1 }), undefined]);
        }
        tableRef.current?.resetToLiveDefault();
    };

    const handleOnRowClick = async (t: Target) => {
        const targetWithHistory = await Targets.getTargetWithHistory(t.id, t.deviceId);
        details.show(`Target Details`, "Okay", targetWithHistory, () => { });
    };
    const handleDownload = () => {
        tableRef.current?.downloadCSV();
    };
    const handleDownloadPvr = () => {
        tableRef.current?.downloadPvrCSV();
    };
    const pvrEnabled = isPvrExportEnabled(facility.organizationId);
    const getDeviceColor = (deviceId: string | undefined) => {
        if (!deviceId) return undefined;
        return facility?.devices?.find(d => d.id === deviceId)?.color ?? "blue"; // default device color is blue
    }

    const handleOnShowTracks = async (target: Target | undefined, show: boolean) => {
        if (!target?.id) return;
        if (show) {
            //If we do not have the history for this, load it...
            const targetWithHistory = await Targets.getTargetWithHistory(target?.id, target?.deviceId);
            if (targetWithHistory) {
                setSelectedTargets((prev) => ({ ...prev, [target.id!]: targetWithHistory }));
            }
        } else {
            //Otherwise they are deselecting this - remove the history
            setSelectedTargets((prev) => (_.omit(prev, target.id!)));
        }
    }
    const facilityShapes = useMemo(() => [
        ...(hideDevices ? [] : facility.devices ?? []),
        ...(hideSL ? [] : facility.screenLines ?? []),
        ...(hideLL ? [] : facility.laneLines ?? []),
        ...(hideDZ ? [] : facility.detectionZones ?? []),
    ] as Array<Device | ScreenLine | DetectionZone | LaneLine>, [facility, hideDevices, hideDZ, hideLL, hideSL]);

    // Live "Select new Targets" can accumulate hundreds of tracks; drawing them all bogs the map down and
    // turns it into spaghetti. Cap the map to the most-recent tracks and fade older ones so the newest
    // read clearly. Sorted by lastUpdate (same key the table sorts by) so map and table agree.
    const MAX_RENDERED_PATHS = 75;
    const OLDEST_PATH_OPACITY = 0.25;
    const recencyOf = (t: Target) => t.lastUpdate?.getTime() ?? t.history?.[t.history.length - 1]?.date?.getTime() ?? t.statusStarted?.getTime() ?? 0;
    const renderedTargets = useMemo(() => {
        return Object.values(selectedTargets)
            .sort((a, b) => recencyOf(b) - recencyOf(a))
            .slice(0, MAX_RENDERED_PATHS);
    }, [selectedTargets]);
    const hiddenPathCount = Math.max(0, Object.keys(selectedTargets).length - renderedTargets.length);


    if (useMapBackground && !isLoading && !centerCoords) {
        return <h1>There was an error loading location details please reload the page</h1>
    }

    return (
        <div className="h-screen flex flex-col">
            <Collapse title="Layout / Live View" height={600} allowFullscreen={true} footer={
                useMapBackground ? (
                    <div className="flex gap-3 place-items-center ms-2">
                        <CheckboxInput label="Show Devices" checked={!hideDevices} onChange={() => setHideDevices(!hideDevices)} />
                        <CheckboxInput label="Show ScreenLines" checked={!hideSL} onChange={() => setHideSL(!hideSL)} />
                        <CheckboxInput label="Show Lane lines" checked={!hideLL} onChange={() => setHideLL(!hideLL)} />
                        <CheckboxInput label="Show Detection Zones" checked={!hideDZ} onChange={() => setHideDZ(!hideDZ)} />
                    </div>
                ) : undefined
            }>
                <div className="relative h-full">
                    <FacilityBackgroundView
                        facility={facility}
                        targets={renderedTargets}
                        mapCenter={centerCoords}
                        mapChildren={<>
                            {facilityShapes.map(mp => (<MapShape facility={facility} key={mp.id} shape={mp} />))}
                            {renderedTargets.map((t, i) => {
                                // Newest = full opacity; ramp toward OLDEST_PATH_OPACITY as tracks approach the cap.
                                const opacity = Math.max(OLDEST_PATH_OPACITY, 1 - (i / (MAX_RENDERED_PATHS - 1)) * (1 - OLDEST_PATH_OPACITY));
                                return <TargetMapShape facility={facility} key={t.id} target={t} color={getDeviceColor(t.deviceId)} opacity={opacity} cased />;
                            })}
                        </>}
                    />
                    {hiddenPathCount > 0 && (
                        <div className="absolute bottom-2 left-2 z-10 bg-black/60 text-white text-xs rounded px-2 py-1 pointer-events-none">
                            Showing newest {renderedTargets.length} tracks ({hiddenPathCount} older hidden)
                        </div>
                    )}
                </div>
            </Collapse>

            <div className="flex-grow mt-5">
                <Collapse title="Targets" height='100%' >
                    <PageContainer title={
                        <div className="flex items-center gap-3">
                            <span>Recent Targets</span>
                            <LiveStatusChip
                                isLive={isLive}
                                srMessage={isLive
                                    ? "Live updates resumed, sorted by date, newest first."
                                    : isDateRange ? "Live updates paused, showing selected date range." : "Live updates paused, table sorted, showing a snapshot."}
                            />
                            {!isLive &&
                                <span className="text-xs font-normal normal-case text-muted-body-text">
                                    {isDateRange ? "Showing selected date range" : "Paused while sorted"}
                                </span>
                            }
                            {!isLive &&
                                <button onClick={handleResumeLive} className="inline-flex items-center gap-1 rounded bg-brand-primary px-2 py-1 text-xs font-semibold normal-case text-white hover:opacity-90" aria-label="Resume live updates">
                                    <span aria-hidden="true">▶</span> Resume live
                                </button>
                            }
                        </div>
                    } Icon={Icons.Target} titleRight={
                        <div className="flex gap-x-5 items-center">
                            <div className="flex items-center mr-4">
                                <span className="mr-2">Show last:</span>
                                <Select options={timeOptions} onChange={handleOnTimeOptionChange} value={hoursToShow} />
                            </div>
                            {hoursToShow <= 0 &&
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">From:</span>
                                    <DateInput
                                        className="w-48"
                                        displayTimezone={facility.localTimezoneId}
                                        value={dateRange[0]?.toISOString()}
                                        showTimeSelect={true}
                                        showTimeInput={false}
                                        timeIntervals={15}
                                        dateFormat="MM/dd/yyyy HH:mm"
                                        onChange={(d) => {
                                            setDateRange((prev) => [d ? zonedTimeToUtc(d, facility.localTimezoneId!) : undefined, prev[1]]);
                                        }}
                                    />
                                    <span className="text-sm font-medium">To:</span>
                                    <DateInput
                                        className="w-48"
                                        displayTimezone={facility.localTimezoneId}
                                        value={dateRange[1]?.toISOString()}
                                        showTimeSelect={true}
                                        showTimeInput={false}
                                        timeIntervals={15}
                                        dateFormat="MM/dd/yyyy HH:mm"
                                        onChange={(d) => {
                                            setDateRange((prev) => [prev[0], d ? zonedTimeToUtc(d, facility.localTimezoneId!) : undefined]);
                                        }}
                                    />
                                </div>
                            }
                            <CheckboxInput label="Select new Targets" disabled={!isLive} checked={isLive && autoSelect} onChange={x => setAutoSelect(prev => !prev)} />
                            {pvrEnabled ? (
                                <DropDownMenu toggleChild={<IconLink Icon={Icons.Download} childrenClassName="inline-flex items-center">Export<Icons.CaretDown className="ml-1" /></IconLink>}>
                                    <IconLink onClick={handleDownload} Icon={Icons.Download}>Recent Targets CSV</IconLink>
                                    <IconLink onClick={handleDownloadPvr} Icon={Icons.Download}>Per-Vehicle Record (MoDOT)</IconLink>
                                </DropDownMenu>
                            ) : (
                                <button onClick={handleDownload}>
                                    <IconLink Icon={Icons.Download}>Download CSV</IconLink>
                                </button>
                            )}
                        </div>
                    }>
                        <div className="overflow-y-auto">
                            <Suspense fallback={<div className="p-4">Loading targets...</div>}>
                                <RecentTargetsTable
                                    mode="facility"
                                    devices={facility.devices ?? []}
                                    laneLines={facility.laneLines ?? []}
                                    facility={facility}
                                    facilityId={facility.id!}
                                    ref={tableRef}
                                    timezoneId={facility.localTimezoneId}
                                    periodStart={dateRange[0]}
                                    periodEnd={dateRange[1]}
                                    onRowClick={handleOnRowClick}
                                    onSelectedChange={handleOnShowTracks}
                                    onSortStateChange={handleSortStateChange}
                                />
                            </Suspense>
                        </div>

                    </PageContainer>
                </Collapse>
            </div>
            {details.renderDialog((val) =>
                <TargetDetailsDialog facility={facility} target={val} onZoomChange={setHideOverflow} />
                , { closeOnOutsideClick: true, showCancelButton: false, className: hideOverflow ? "overflow-hidden max-h-screen w-full max-w-3xl" : undefined })}
        </div>
    );
}